// 013A/013B — Anatomía Patológica (Fase 7, Tanda 4, ver PLAN.md). Transcripción campo por campo del PDF oficial del
// Anexo 1 MSP: 013A hoja idx 59 y 013B hoja idx 66 (footer `SNS-MSP/HCU-form.013A|013B/2021`, 1 hoja cada una).
// Informe de brechas en `docs/hce-fase7/brechas-010-012-013.md`.
//
// Lo que el papel NO trae y se retiró de la UI (estaba inventado): el interruptor "Es una citología vaginal"
// (013A: el bloque de citología vaginal va siempre, como en el papel), "Tratamiento previo" (013A; el papel
// tiene "Tratamiento" dentro del resumen clínico) y "N° de informe" (013B). Los recursos ya guardados no se tocan.
//
// FHIR (consumidores reales: `LabsTab`, `SubirDocumento`, `SelectorRecurso`): 013A sigue creando un `ServiceRequest`
// (status `active`, intent `order`, categoría AP, `code.text` legible, `priority`, `reasonCode` por diagnóstico);
// 013B un `DiagnosticReport` cuyo `conclusion` lleva macroscopía, microscopía y diagnóstico. Todo lo demás va como
// `Par[]` de respaldo en el PDF firmado.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import {
  MARCAS_PRE_DEF,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const fecha = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'fecha' });
const opciones = (...etiquetas: readonly string[]): readonly OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));

const OPCIONES_SERVICIO = opciones('Emergencia', 'Consulta externa', 'Hospitalización');

// ---------- 013A ----------

export const CAMPOS_SERVICIO_013A: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: OPCIONES_SERVICIO },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'prioridad', etiqueta: 'Prioridad', tipo: 'select', opciones: opciones('Urgente', 'Rutina') },
  { clave: 'horaToma', etiqueta: 'Hora de toma', tipo: 'hora' },
  fecha('fechaToma', 'Fecha de toma'),
];

/** Bloque C: estudio solicitado (multiselección) con "Otros tipo de estudio" y descripción. */
export const CAMPOS_ESTUDIO_013A: readonly CampoSimple[] = [
  casilla('histopatologia', 'Histopatología'),
  casilla('citologia', 'Citología'),
  casilla('revisionLaminillas', 'Revisión de laminillas'),
  casilla('transoperatorio', 'Transoperatorio'),
  { clave: 'otroEstudio', etiqueta: 'Otros tipo de estudio' },
  area('descripcion', 'Descripción'),
];

const CLAVES_ESTUDIO_013A = ['histopatologia', 'citologia', 'revisionLaminillas', 'transoperatorio'] as const;

/** Bloque D: resumen clínico — el papel pide llenar todos los campos. */
export const CAMPOS_RESUMEN_013A: readonly CampoSimple[] = [
  area('cuadroClinico', 'Cuadro clínico'),
  area('resultadosLaboratorio', 'Resultados de laboratorio'),
  area('datosImagen', 'Datos de imagen'),
  area('tratamiento', 'Tratamiento'),
  area('procedimientoMuestra', 'Procedimiento realizado para obtención de muestra'),
];

export const NUM_DIAGNOSTICOS_013A = 5;

/** Bloque F: origen y número de muestras enviadas. */
export const CAMPOS_MUESTRA_013A: readonly CampoSimple[] = [area('muestra', 'Muestra o pieza / número (origen y número de muestras enviadas)')];

/** Bloque G: datos básicos para citología vaginal. */
export const CAMPOS_MATERIAL_CITOLOGIA: readonly CampoSimple[] = [
  casilla('endocervix', 'Endocérvix'),
  casilla('exocervix', 'Exocérvix'),
  casilla('paredVaginal', 'Pared vaginal'),
  casilla('unionEscamocolumnar', 'Unión escamocolumnar'),
  casilla('munonCervical', 'Muñón cervical'),
];

export const CAMPOS_ANTICONCEPCION_CITOLOGIA: readonly CampoSimple[] = [
  casilla('oralInyectable', 'Oral o inyectable'),
  casilla('diu', 'DIU'),
  casilla('ligadura', 'Ligadura'),
  casilla('barrera', 'Barrera'),
  casilla('implante', 'Implante'),
  casilla('otroAnticonceptivo', 'Otro'),
  casilla('terapiaHormonal', 'Terapia hormonal'),
];

export const CAMPOS_HISTORIA_CITOLOGIA: readonly CampoSimple[] = [
  numero('menarquia', 'Edad de menarquia'),
  numero('menopausia', 'Edad de menopausia'),
  numero('inicioVidaSexual', 'Edad de inicio de vida sexual'),
  numero('gestas', 'Gestas'),
  numero('partos', 'Partos'),
  numero('abortos', 'Abortos'),
  numero('cesareas', 'Cesáreas'),
  fecha('fum', 'Fecha de última menstruación (FUM)'),
  fecha('ultimoParto', 'Fecha del último parto'),
  fecha('ultimaCitologia', 'Fecha de la última citología'),
];

export const CAMPOS_DESCRIPCION_CITOLOGIA: readonly CampoSimple[] = [
  area('descripcionMacroscopica', 'Descripción macroscópica'),
  { clave: 'diagnosticoUltimaCitologia', etiqueta: 'Diagnóstico de última citología' },
];

/** Bloque H. */
export const CAMPOS_BIOLOGIA_MOLECULAR_013A: readonly CampoSimple[] = [area('biologiaMolecular', 'Biología molecular y genética')];

/** Bloque I (parte de recepción; el profesional que solicita lo arma el servidor). */
export const CAMPOS_RECEPCION_013A: readonly CampoSimple[] = [
  fecha('fechaRecepcion', 'Fecha de recepción'),
  { clave: 'horaRecepcion', etiqueta: 'Hora de recepción', tipo: 'hora' },
  { clave: 'quienEntrega', etiqueta: 'Nombre y apellido de quien entrega la muestra' },
  { clave: 'quienRecibe', etiqueta: 'Nombre y apellido de quien recibe la muestra' },
  { clave: 'codigoInterno', etiqueta: 'Código interno de muestra' },
];

export interface Estado013A {
  readonly servicio: Valores;
  readonly estudio: Valores;
  readonly resumen: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly muestra: Valores;
  readonly material: Valores;
  readonly anticoncepcion: Valores;
  readonly historia: Valores;
  readonly descripcionCitologia: Valores;
  readonly biologiaMolecular: Valores;
  readonly recepcion: Valores;
}

export function estadoInicial013A(): Estado013A {
  return {
    servicio: valoresVacios(CAMPOS_SERVICIO_013A),
    estudio: valoresVacios(CAMPOS_ESTUDIO_013A),
    resumen: valoresVacios(CAMPOS_RESUMEN_013A),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_013A }, () => diagnosticoConMarcaVacio()),
    muestra: valoresVacios(CAMPOS_MUESTRA_013A),
    material: valoresVacios(CAMPOS_MATERIAL_CITOLOGIA),
    anticoncepcion: valoresVacios(CAMPOS_ANTICONCEPCION_CITOLOGIA),
    historia: valoresVacios(CAMPOS_HISTORIA_CITOLOGIA),
    descripcionCitologia: valoresVacios(CAMPOS_DESCRIPCION_CITOLOGIA),
    biologiaMolecular: valoresVacios(CAMPOS_BIOLOGIA_MOLECULAR_013A),
    recepcion: valoresVacios(CAMPOS_RECEPCION_013A),
  };
}

/** Prioridad de FHIR: el papel imprime Urgente / Rutina. */
export function prioridadFhir013A(prioridad: string | undefined): 'urgent' | 'routine' {
  return prioridad === 'Urgente' ? 'urgent' : 'routine';
}

/** Tipos de estudio marcados (más "Otros" si se escribió), en el orden impreso. */
export function estudiosMarcados013A(estudio: Valores): string[] {
  const marcados = CLAVES_ESTUDIO_013A.filter((c) => estudio[c] === 'true').map((c) => CAMPOS_ESTUDIO_013A.find((x) => x.clave === c)!.etiqueta);
  const otro = (estudio.otroEstudio ?? '').trim();
  return otro ? [...marcados, otro] : marcados;
}

/** `ServiceRequest.code.text`: los estudios marcados y la muestra — `LabsTab` lo muestra tal cual. */
export function textoSolicitud013A(e: Estado013A): string {
  const estudios = estudiosMarcados013A(e.estudio).join(', ') || 'Anatomía patológica';
  const muestra = (e.muestra.muestra ?? '').trim();
  return muestra ? `${estudios}: ${muestra}` : estudios;
}

const paresDiagnosticos = (titulo: string, dx: readonly DiagnosticoConMarca[]): Par[] =>
  dx.map((d, i) => ({
    pregunta: `${titulo} ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));

export function paresDe013A(e: Estado013A): Par[] {
  return [
    ...paresDeCampos(CAMPOS_SERVICIO_013A, e.servicio),
    ...paresDeCampos(CAMPOS_ESTUDIO_013A, e.estudio),
    ...paresDeCampos(CAMPOS_RESUMEN_013A, e.resumen),
    ...paresDiagnosticos('Diagnóstico', e.diagnosticos),
    ...paresDeCampos(CAMPOS_MUESTRA_013A, e.muestra),
    ...paresDeCampos(CAMPOS_MATERIAL_CITOLOGIA, e.material, 'Citología vaginal — material: '),
    ...paresDeCampos(CAMPOS_ANTICONCEPCION_CITOLOGIA, e.anticoncepcion, 'Citología vaginal — anticoncepción: '),
    ...paresDeCampos(CAMPOS_HISTORIA_CITOLOGIA, e.historia, 'Citología vaginal — '),
    ...paresDeCampos(CAMPOS_DESCRIPCION_CITOLOGIA, e.descripcionCitologia, 'Citología vaginal — '),
    ...paresDeCampos(CAMPOS_BIOLOGIA_MOLECULAR_013A, e.biologiaMolecular),
    ...paresDeCampos(CAMPOS_RECEPCION_013A, e.recepcion),
  ].filter((p) => p.respuesta.trim() !== '');
}

// ---------- 013B ----------

export const CAMPOS_SERVICIO_013B: readonly CampoSimple[] = [
  { clave: 'establecimientoSolicitante', etiqueta: 'Establecimiento de salud solicitante' },
  { clave: 'profesionalSolicitante', etiqueta: 'Profesional solicitante' },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'codigoInterno', etiqueta: 'Código interno de muestra de anatomía patológica' },
];

export const CAMPOS_MACROSCOPICA_013B: readonly CampoSimple[] = [
  area('macroscopica', 'Descripción macroscópica'),
  { clave: 'responsableMacroscopia', etiqueta: 'Nombre del profesional responsable de la macroscopía' },
];

export const CAMPOS_MICROSCOPICA_013B: readonly CampoSimple[] = [
  area('microscopica', 'Descripción microscópica'),
  { clave: 'responsableMicroscopia', etiqueta: 'Nombre del profesional responsable de la microscopía' },
];

/** Bloque E: informe de citología vaginal. */
export const CAMPOS_CITOLOGIA_013B: readonly CampoSimple[] = [
  { clave: 'bethesda', etiqueta: 'Clasificación Bethesda', tipo: 'select', opciones: opciones('Normal', 'LIE bajo', 'LIE alto', 'CA') },
  { clave: 'bethesdaTexto', etiqueta: 'Clasificación Bethesda — especificación' },
  casilla('muestraInadecuada', 'Muestra inadecuada'),
  casilla('hongos', 'Hongos'),
  casilla('eritrocitos', 'Eritrocitos'),
  casilla('floraBacteriana', 'Flora bacteriana'),
  casilla('histiocitos', 'Histiocitos'),
  { clave: 'numeroMuestra', etiqueta: 'Número de muestra' },
  fecha('fechaProceso', 'Fecha de proceso'),
  area('observaciones', 'Observaciones'),
];

export const CAMPOS_DIAGNOSTICO_013B: readonly CampoSimple[] = [area('diagnostico', 'Diagnóstico (y código CIE)')];
export const CAMPOS_RECOMENDACIONES_013B: readonly CampoSimple[] = [area('recomendaciones', 'Recomendaciones')];

export interface Estado013B {
  readonly servicio: Valores;
  readonly macroscopica: Valores;
  readonly microscopica: Valores;
  readonly citologia: Valores;
  readonly diagnostico: Valores;
  readonly recomendaciones: Valores;
}

export function estadoInicial013B(): Estado013B {
  return {
    servicio: valoresVacios(CAMPOS_SERVICIO_013B),
    macroscopica: valoresVacios(CAMPOS_MACROSCOPICA_013B),
    microscopica: valoresVacios(CAMPOS_MICROSCOPICA_013B),
    citologia: valoresVacios(CAMPOS_CITOLOGIA_013B),
    diagnostico: valoresVacios(CAMPOS_DIAGNOSTICO_013B),
    recomendaciones: valoresVacios(CAMPOS_RECOMENDACIONES_013B),
  };
}

/** `DiagnosticReport.conclusion` (lo lee `LabsTab`): macroscopía, microscopía y diagnóstico, en ese orden. */
export function conclusionInforme013B(e: Estado013B): string | undefined {
  const macro = (e.macroscopica.macroscopica ?? '').trim();
  const micro = (e.microscopica.microscopica ?? '').trim();
  const dx = (e.diagnostico.diagnostico ?? '').trim();
  const texto = [macro, micro, dx ? `Diagnóstico: ${dx}` : ''].filter(Boolean).join('\n\n');
  return texto || undefined;
}

export function paresDe013B(e: Estado013B): Par[] {
  return [
    ...paresDeCampos(CAMPOS_SERVICIO_013B, e.servicio),
    ...paresDeCampos(CAMPOS_MACROSCOPICA_013B, e.macroscopica),
    ...paresDeCampos(CAMPOS_MICROSCOPICA_013B, e.microscopica),
    ...paresDeCampos(CAMPOS_CITOLOGIA_013B, e.citologia, 'Citología vaginal — '),
    ...paresDeCampos(CAMPOS_DIAGNOSTICO_013B, e.diagnostico),
    ...paresDeCampos(CAMPOS_RECOMENDACIONES_013B, e.recomendaciones),
  ].filter((p) => p.respuesta.trim() !== '');
}
